import "./user-edit.scss";
import { SHA256 } from "crypto-js";
import { useState, useEffect, FormEvent, useContext } from "react";
import { Button, Form } from "react-bootstrap";
import { BsArrowLeft, BsKey, BsSave, BsTrash } from "react-icons/bs";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";

import { ITableRowData, IColumn } from "../../../shared/components/normal-table/normal-table";
import SimpleModal from "../../../shared/components/simple-modal/simple-modal";
import Spinner from "../../../shared/components/spinner/spinner";
import Tooltip from "../../../shared/components/tooltip/tooltip";
import { roleAdmin } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { getUser, postSingleNonTable, deleteMultiple, failedToast } from "../../../shared/utils/api";
import { RenderFields } from "../../../shared/utils/render-fields";
import { activeOptions } from "../../../shared/utils/static-values";
import { useBack } from "../../../shared/utils/use-back";
import { validateLoadingStatusNew } from "../../../shared/utils/utils";


const fields: IColumn[] = [
  {
    key: "name",
    displayName: "First Name",
    dataType: {
      type: "text",
    },
  },
  {
    key: "last_name",
    displayName: "Last name",
    dataType: {
      type: "text",
    },
  },
  {
    key: "email",
    displayName: "Email Address",
    dataType: {
      type: "text",
    },
    disabled: true
  },
  {
    key: "active_status",
    displayName: "Active",
    dataType: {
      type: "enumeration",
      values: activeOptions,
    },
  },
  {
    key: "admin",
    displayName: "Technical admin",
    dataType: {
      type: "enumeration",
      values: activeOptions,
    },
    noEmptyDefault: true
  },
];

const passwordModalFields: IColumn[] = [
  {
    key: "new_password",
    displayName: "New password",
    dataType: {
      type: "text",
    },
    subType: "password"
  },
  {
    key: "confirm_new_password",
    displayName: "Confirm new password",
    dataType: {
      type: "text",
    },
    subType: "password"
  }
];

const UserEdit = () => {
  const [changed, setChanged] = useState(false);
  const [validated, setValidated] = useState(false);
  const [data, setData] = useState<ITableRowData>({
    id: "",
    name: "",
    last_name: "",
    groups: [],
    roles: [],
  });
  const authCtx = useContext(AuthContext);
  const hasSystemAdminRole = authCtx.systemAdmin;
  const hasTenantAdminRole = authCtx.selectedTenant.roles.indexOf(roleAdmin) !== -1;

  const params = useParams();
  const showChangePasswordButton = (hasSystemAdminRole || hasTenantAdminRole) && authCtx.userId !== params.id;
  const { sendRequest: getSingleDataRequest, status: singleStatus, data: loadedData } = useHttp(getUser, true, authCtx.token);
  const { sendRequest: postSingleRequest, status: allStatusPost, error: errorPost } = useHttp(postSingleNonTable, false, authCtx.token);
  const { sendRequest: postChangePasswordRequest, status: allStatusChangePassword, error: errorChangePassword } = useHttp(postSingleNonTable, false, authCtx.token);
  const { sendRequest: deleteMultipleRequest, status: allStatusDelete, error: errorDelete } = useHttp(deleteMultiple, false, authCtx.token);

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    id: "",
    new_password: "",
    confirm_new_password: "",
  });

  const { handleBack } = useBack();
  const navigate = useNavigate();

  useEffect(() => {
    getSingleDataRequest(`user/${params.id}`);
  }, [getSingleDataRequest]);

  useEffect(() => {
    if (loadedData && data.name) {
      if (
        loadedData.last_name !== data.last_name ||
        loadedData.name !== data.name ||
        loadedData.email !== data.email ||
        loadedData.active_status !== data.active_status ||
        loadedData.admin !== data.admin
      ) {
        setChanged(true);
      } else {
        setChanged(false);
      }
    }
  }, [data]);

  useEffect(() => {
    if (allStatusChangePassword === "completed" && !errorChangePassword) {
      setShowChangePasswordModal(false);
    }
  }, [allStatusChangePassword, errorChangePassword]);

  useEffect(() => {
    if (allStatusPost === "completed" && !errorPost) {
      setChanged(false);
      authCtx.userUpdate();
      navigate("/users");
    }
  }, [allStatusPost]);

  useEffect(() => {
    if (allStatusDelete === "completed" && !errorDelete) {
      navigate("/users");
    }
  }, [allStatusDelete]);

  useEffect(() => {
    if (loadedData) {
      setData({...data, ...loadedData});
    }
  }, [loadedData]);
  
  const handleOnChange = (targetVal: string | boolean | null, inputKey: string) => {
    setData((prevState) => ({ ...prevState, [inputKey]: targetVal }));
  };

  const getData = (url: string) => {
    console.log(url);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity()) {
      if (changed) {
        postSingleRequest(`user/${params.id}`, {id: data.id, name: data.name, last_name: data.last_name, email:data.email, active_status: data.active_status, admin: data.admin});
      }
    }
    setValidated(true);
  };

  const renderTenantWithRoles = () => (
    loadedData.tenants.map((row: ITableRowData) => {
      const roles = row.roles && row.roles.sort().map((role: string, index:number) => <span className="userEdit-role" key={index}>{role}</span>);
      return <div onClick={() =>navigate(`/tenants/${row.id}`)} className="userEdit-tenant" key={row.id}>{row.name}{roles}</div>;
    })
  );

  const handleOnDelete = (ids: string[]) => {
    deleteMultipleRequest("users", { ids });
  };

  const handleChangePasswordSubmit = () => {
    if (changePasswordData.new_password === changePasswordData.confirm_new_password) {
      const newPassword = SHA256(changePasswordData.new_password).toString();
      postChangePasswordRequest(`update_password_for_user/${params.id}`, {new_password: newPassword});
    } else {
      failedToast("Passwords do not match");
    }
  };

  const handleOnChangePasswordChange = (targetVal: string | boolean | null, inputKey: string) => {
    setChangePasswordData({ ...changePasswordData, [inputKey]: targetVal });
  };

  const renderFields = () => {
    if (fields.length !== 0) {
      return (
        <RenderFields
          mode="aside"
          fields={fields}
          data={data}
          getData={getData}
          handleOnChange={handleOnChange}
        />
      );
    }
  };

  const renderChangePasswordModalBody = () => {
    return (
      <RenderFields
        mode="aside"
        fields={passwordModalFields}
        data={changePasswordData}
        handleOnChange={handleOnChangePasswordChange}
      />
    );
  };

  return (
    <div className="userEdit">
      {validateLoadingStatusNew([singleStatus, allStatusDelete, allStatusPost, allStatusChangePassword]) && <Spinner />}

      <div onClick={() => handleBack()} className="listPage-nav-btn">
        <BsArrowLeft />
      </div>
      <SimpleModal
        modalBody={renderChangePasswordModalBody()}
        buttonText={"Update"}
        mode={"Update Password"}
        handleModalClosed={() => setShowChangePasswordModal(false)}
        handleSubmit={handleChangePasswordSubmit}
        show={showChangePasswordModal}
      />
      <div className="userEdit-header">
        <h1 className="mt-2 mb-3">
          {data.name? (`${data.name} ${data.last_name}`) : "Edit User"}
          <Tooltip content={"When modifying the user data"} direction="top" delay={350}  />
        </h1>
      </div>

      <div className="userEdit-form">
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
        
          <div className="userEdit-buttonsGroup">

            <span className="userEdit-info-wrapper"><span className="userEdit-info">Account info</span></span>

            <Tooltip content={"Delete"} direction="top" delay={350} >
              <Button className="userEdit-delete" onClick={() => params.id && handleOnDelete([params.id])} variant="primary">
                <BsTrash />
              </Button>
            </Tooltip>
            {showChangePasswordButton && <Tooltip content={"Change password"} direction="top" delay={350} >
              <Button className="userEdit-changePassword" onClick={() => setShowChangePasswordModal(true)} variant="primary">
                <BsKey />
              </Button>
            </Tooltip>}

            <Tooltip content={"Save"} direction="top" delay={350} >
              <Button className="userEdit-save" type="submit" variant="primary">
                <BsSave />
              </Button>
            </Tooltip>
          </div>
          {renderFields()}

          <div>            
            <label className="userEdit-tenants-label">Tenants of the user</label>
            <div className="userEdit-tenants-wrapper">
              {loadedData && renderTenantWithRoles()}
            </div>
          </div>

        </Form>

      </div>
    </div>
  );
};

export default UserEdit;
